import { CONFIG_VARS } from "../../constants/urlConstants/configuration";
import {
  EDITABLE_FIELDS_BY_STATE,
  LOCKED_EDIT_STATES,
  SCHOOL_EDIT_ALLOWED_ROLES,
} from "../../constants/schoolEditConstants";

export const UDISE_CODE_REGEX = /^\d{11}$/;
export const UDISE_CODE_LENGTH = 11;

export const validateUdiseCode = (udiseCode, { required = true } = {}) => {
  const digits = String(udiseCode ?? "").replace(/\D/g, "");

  if (!digits) {
    return required ? "UDISE code is required" : null;
  }

  if (digits.length !== UDISE_CODE_LENGTH) {
    const remaining = UDISE_CODE_LENGTH - digits.length;
    return remaining > 0
      ? `Enter ${remaining} more digit${remaining === 1 ? "" : "s"} (UDISE code is 11 digits)`
      : "UDISE code must be exactly 11 digits";
  }

  if (!UDISE_CODE_REGEX.test(digits)) {
    return "UDISE code must be exactly 11 digits";
  }

  return null;
};
export const EMAIL_ALLOWED_TLDS = Object.freeze([
  "com",
  "in",
  "org",
  "net",
  "edu",
  "co.in",
  "ac.in",
  "gov.in",
  "me",
  "io",
]);

export const validateEmailField = (email, { required = true } = {}) => {
  const trimmed = email?.trim() || "";

  if (!trimmed) {
    return required ? "Email is required" : null;
  }

  if (/[^a-zA-Z0-9@._-]/.test(trimmed)) {
    return "Email must not contain special characters except @, ., - and _";
  }

  if (!trimmed.includes("@")) {
    return "Email must contain @";
  }

  const [localPart, domainPart] = trimmed.split("@");
  if (!localPart || !domainPart || trimmed.split("@").length > 2) {
    return "Please enter a valid email address";
  }

  const domain = domainPart.toLowerCase();
  const hasValidTld = EMAIL_ALLOWED_TLDS.some(
    (tld) => domain.endsWith(`.${tld}`) || domain === tld,
  );

  if (!hasValidTld) {
    return "Email must end with a valid domain (.com, .in, .org, .net, .edu, .co.in)";
  }

  if (!/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(trimmed)) {
    return "Please enter a valid email address";
  }

  return null;
};

export const getPincodeConfig = (countryName = "") => {
  const normalized = countryName.trim().toLowerCase();

  if (
    normalized.includes("united arab emirates") ||
    normalized === "uae"
  ) {
    return {
      length: 5,
      label: "Pincode must be exactly 5 digits",
      incompleteLabel: (remaining) =>
        `Enter ${remaining} more digit${remaining === 1 ? "" : "s"} (UAE pincode is 5 digits)`,
    };
  }

  if (normalized.includes("india")) {
    return {
      length: 6,
      label: "Pincode must be exactly 6 digits",
      incompleteLabel: (remaining) =>
        `Enter ${remaining} more digit${remaining === 1 ? "" : "s"} (India pincode is 6 digits)`,
    };
  }

  return {
    length: 6,
    label: "Pincode must be exactly 6 digits",
    incompleteLabel: (remaining) =>
      `Enter ${remaining} more digit${remaining === 1 ? "" : "s"}`,
  };
};

export const validatePincodeFormat = (pincode, countryName = "") => {
  const digits = String(pincode ?? "").replace(/\D/g, "");
  const { length, label } = getPincodeConfig(countryName);

  if (!digits) {
    return "Pincode is required";
  }

  if (digits.length !== length) {
    return label;
  }

  return null;
};

export const isPincodeComplete = (pincode, countryName = "") => {
  const digits = String(pincode ?? "").replace(/\D/g, "");
  return digits.length === getPincodeConfig(countryName).length;
};

export const canUserEditSchool = (roleIds = []) => {
  const roleMappings = CONFIG_VARS.roleMappings[1];
  if (!roleIds.length || !roleMappings) return false;

  return roleIds.some((roleId) =>
    SCHOOL_EDIT_ALLOWED_ROLES.includes(roleMappings[roleId]),
  );
};

export const isEditButtonDisabled = (schoolEditState) =>
  LOCKED_EDIT_STATES.includes(schoolEditState);

export const getEditableFields = (schoolEditState) =>
  EDITABLE_FIELDS_BY_STATE[schoolEditState] || [];

export const isFieldEditable = (fieldKey, schoolEditState, isEditMode) => {
  if (!isEditMode) return false;
  return getEditableFields(schoolEditState).includes(fieldKey);
};

const normalizeFieldValue = (field, value) => {
  if (field === "pincode") {
    return String(value ?? "").trim();
  }
  return String(value ?? "").trim();
};

const serializeFieldValue = (field, value) => {
  if (field === "pincode") {
    return Number(value) || 0;
  }
  if (field === "udise_code") {
    return value?.toString().trim() || "";
  }
  return typeof value === "string" ? value.trim() : value;
};

export const hasFormChanges = (formData, originalFormData, editableFields) =>
  editableFields.some(
    (field) =>
      normalizeFieldValue(field, formData[field]) !==
      normalizeFieldValue(field, originalFormData[field]),
  );

const GEO_ID_FIELDS = Object.freeze({
  country: "country_id",
  state: "state_id",
  district: "district_id",
  city: "city_id",
});

export const buildUpdateSchoolPayload = (
  formData,
  originalFormData,
  editableFields,
) => {
  const payload = { school_id: formData.school_id };

  editableFields.forEach((field) => {
    if (field === "tier") return;

    if (
      normalizeFieldValue(field, formData[field]) !==
      normalizeFieldValue(field, originalFormData[field])
    ) {
      payload[field] = serializeFieldValue(field, formData[field]);

      const idField = GEO_ID_FIELDS[field];
      if (idField && formData[idField]) {
        payload[idField] = formData[idField];
      }
    }
  });

  return payload;
};

export const validateSchoolEditForm = (formData, editableFields) => {
  const errors = {};

  const requireField = (key, label) => {
    if (!editableFields.includes(key)) return;
    const value = formData[key];
    if (value === undefined || value === null || String(value).trim() === "") {
      errors[key] = `${label} is required`;
    }
  };

  requireField("school_name", "School name");
  requireField("board", "Board");
  requireField("school_type", "School type");
  requireField("branch_name", "Branch name");
  requireField("address", "Address");

  if (editableFields.includes("country")) {
    if (!formData.country?.trim()) {
      errors.country = "Country is required";
    } else if (!formData.country_id) {
      errors.country = "Please select a valid country";
    }
  }

  if (editableFields.includes("state")) {
    if (!formData.state?.trim()) {
      errors.state = "State is required";
    } else if (!formData.state_id) {
      errors.state = "Please select a valid state";
    }
  }

  if (editableFields.includes("district")) {
    if (!formData.district?.trim()) {
      errors.district = "District is required";
    } else if (!formData.district_id) {
      errors.district = "Please select a valid district";
    }
  }

  if (editableFields.includes("city")) {
    if (!formData.city?.trim()) {
      errors.city = "City is required";
    } else if (!formData.city_id) {
      errors.city = "Please select a valid city";
    }
  }

  if (editableFields.includes("email")) {
    const emailError = validateEmailField(formData.email, { required: true });
    if (emailError) {
      errors.email = emailError;
    }
  }

  if (editableFields.includes("udise_code")) {
    const udiseError = validateUdiseCode(formData.udise_code, { required: true });
    if (udiseError) {
      errors.udise_code = udiseError;
    }
  }

  if (editableFields.includes("pincode")) {
    if (!formData.district_id) {
      errors.district = "Please select a district before entering pincode";
    }
    if (!formData.city_id) {
      errors.city = "Please select a city before entering pincode";
    }

    const pincodeError = validatePincodeFormat(
      formData.pincode,
      formData.country,
    );
    if (pincodeError) {
      errors.pincode = pincodeError;
    }
  }

  return errors;
};

export const isSchoolEditFormValid = (formData, editableFields) =>
  Object.keys(validateSchoolEditForm(formData, editableFields)).length === 0;

export const createFormDataFromSchool = (school) => ({
  school_id: school.school_id || "",
  school_name: school.school_name || "",
  udise_code: school.udise_code || "",
  school_type: school.school_type || "",
  board: school.board || "",
  branch_name: school.branch_name || "",
  address: school.address || "",
  city: school.city || "",
  district: school.district || "",
  state: school.state || "",
  country: school.country || "",
  country_id: school.country_id?.toString() || "",
  state_id: school.state_id?.toString() || "",
  district_id: school.district_id?.toString() || "",
  city_id: school.city_id?.toString() || "",
  pincode: school.pincode?.toString() || "",
  email: school.email || "",
});
